import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../AuthContext";
import FilterBar from "../components/FilterBar";
import ProjectModal from "../components/ProjectModal";
import "./ProjectPage.css";

const COLUMNS = [
  { id: "backlog", title: "Backlog", dbValue: "Backlog" },
  { id: "todo", title: "To Do", dbValue: "To Do" },
  { id: "inprogress", title: "In Progress", dbValue: "In Progress" },
  { id: "review", title: "Review", dbValue: "Review" },
  { id: "done", title: "Done", dbValue: "Done" }
];

const ITEMS_PER_PAGE = 4;

const INITIAL_FORM_STATE = {
  project_name: "",
  project_code: "",
  location: "",
  status: "Backlog",
  project_type: "Grouped",
  meta_id: "",         
  new_client_name: "",  
  assigned_user_ids: [], 
  km: "",               
  office_lead: "",      
  site_lead: "",        
  field_activity: "",   // Acts as Start Date
  field_activity_end: "",  // Acts as End Date
  kml: "",              
  report: "",           
  submission: "",       
  bill: ""              
};

function ProjectPage() {
  const { user, loading: authLoading } = useAuth();

  const [projectsList, setProjectsList] = useState([]);
  const [clients, setClients] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]); 
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  const [searchTerm, setSearchTerm] = useState("");
  
  // Track pagination states individually for each Kanban column
  const [columnPages, setColumnPages] = useState({
    backlog: 1,
    todo: 1,
    inprogress: 1,
    review: 1,
    done: 1
  });

  const isSuperAdmin = user?.role === "super_admin";

  // ---------------- FETCH PROJECTS ----------------
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          *,
          project_meta (
            meta_id,
            client_name
          ),
          project_assignments (
            users (
              id,
              name,
              email
            )
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProjectsList(data || []);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------- FETCH CLIENTS ----------------
  const fetchClients = useCallback(async () => {
    const { data, error } = await supabase
      .from("project_meta")
      .select("meta_id, client_name")
      .order("client_name", { ascending: true });

    if (!error) setClients(data || []);
  }, []);

  // ---------------- FETCH USERS FOR ASSIGNMENT ----------------
  const fetchSystemUsers = useCallback(async () => {
    const { data, error } = await supabase
      .from("users") 
      .select("id, name, email")
      .order("name", { ascending: true });

    if (!error) setSystemUsers(data || []);
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchProjects();
      fetchClients();
      if (user.role === "super_admin") {
        fetchSystemUsers();
      }
    }
  }, [authLoading, user, fetchProjects, fetchClients, fetchSystemUsers]);

  // ---------------- STATUS CHANGE ----------------
  const handleStatusChange = async (projectId, newStatus) => {
    if (!isSuperAdmin) return alert("Only Super Admin allowed");

    const { error } = await supabase
      .from("projects")
      .update({ status: newStatus })
      .eq("project_id", projectId);

    if (error) return alert(error.message);

    setProjectsList((prev) =>
      prev.map((p) =>
        p.project_id === projectId ? { ...p, status: newStatus } : p
      )
    );
  };

  // ---------------- EDIT ----------------
  const handleEditProject = (project) => {
  if (!isSuperAdmin) return alert("Only Super Admin can edit projects");

  const currentAssignedIds = project.project_assignments
    ? project.project_assignments.map((pa) => pa.users?.id).filter(Boolean)
    : [];

  const actualMetaId = project.meta_id || project.project_meta?.meta_id || "";

  // Helper helper formatting database dates cleanly to YYYY-MM-DD for input compatibility
  const formatForInput = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0]; // Truncates timezone fragments safely
  };

  setEditingProject(project);
  setFormData({
    project_name: project.project_name || "",
    project_code: project.project_code || "",
    location: project.location || "",
    status: project.status || "Backlog",
    project_type: project.project_type || "Grouped",
    meta_id: actualMetaId,
    new_client_name: "", 
    assigned_user_ids: currentAssignedIds, 
    km: project.km || "",                                                                    
    office_lead: project.office_lead || "",        
    site_lead: project.site_lead || "",            
    field_activity: formatForInput(project.field_activity), // Restructured
    field_activity_end: formatForInput(project.field_activity_end), // Restructured
    kml: project.kml || "",                                
    report: project.report || "",                                  
    submission: project.submission || "",           
    bill: project.bill || ""                                
  });
  setShowForm(true);
};

  // ---------------- SAVE / UPDATE ----------------
  const handleSaveProject = async () => {
    if (!isSuperAdmin) return alert("Unauthorized action.");
    if (!formData.project_name) return alert("Project Name is required");
    
    if (!formData.meta_id && (!formData.new_client_name || !formData.new_client_name.trim())) {
      return alert("Please pick an existing client or create a new one.");
    }

    try {
      let finalMetaId = formData.meta_id;

      if (formData.new_client_name && formData.new_client_name.trim() !== "") {
        const { data: newClient, error: clientError } = await supabase
          .from("project_meta")
          .insert([{ client_name: formData.new_client_name.trim() }])
          .select()
          .single();

        if (clientError) throw clientError;
        finalMetaId = newClient.meta_id;
      }

      const projectPayload = {
        project_name: formData.project_name,
        project_code: formData.project_code,
        location: formData.location,
        status: formData.status,
        meta_id: finalMetaId,
        project_type: formData.project_type,
        km: formData.km,                        
        office_lead: formData.office_lead,      
        site_lead: formData.site_lead,          
        field_activity: formData.field_activity, 
        field_activity_end: formData.field_activity_end, 
        kml: formData.kml,                        
        report: formData.report,                 
        submission: formData.submission,          
        bill: formData.bill,  
        user_id: user?.id ? user.id : null, 
        user_name: user?.name || user?.email || null,       
      };

      let activeProjectId = editingProject?.project_id;

      if (editingProject) {
        const { error } = await supabase
          .from("projects")
          .update(projectPayload)
          .eq("project_id", editingProject.project_id);

        if (error) throw error;
      } else {
        const { data: createdProject, error } = await supabase
          .from("projects")
          .insert([projectPayload])
          .select()
          .single();

        if (error) throw error;
        activeProjectId = createdProject.project_id;
      }

      const { error: deleteAssignError } = await supabase
        .from("project_assignments")
        .delete()
        .eq("project_id", activeProjectId);

      if (deleteAssignError) throw deleteAssignError;

      if (formData.assigned_user_ids && formData.assigned_user_ids.length > 0) {
        const structuralAssignments = formData.assigned_user_ids.map((uId) => ({
          project_id: activeProjectId,
          user_id: uId
        }));

        const { error: bulkInsertError } = await supabase
          .from("project_assignments")
          .insert(structuralAssignments);

        if (bulkInsertError) throw bulkInsertError;
      }

      resetForm();
      await fetchClients();
      await fetchProjects();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---------------- DELETE ----------------
  const handleDeleteProject = async (id) => {
    if (!isSuperAdmin) return alert("Only Super Admin allowed");
    if (!window.confirm("Delete this project?")) return;

    const { error } = await supabase
      .from("projects")
      .delete()
      .eq("project_id", id);

    if (error) return alert(error.message);

    fetchProjects();
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProject(null);
    setFormData(INITIAL_FORM_STATE);
  };

  // ---------------- CSV EXPORT HELPERS ----------------
  const generateCSVBlob = (headers, rowsData, fileName) => {
    const csvRows = [headers.join(",")];
    rowsData.forEach(row => csvRows.push(row.join(",")));
    
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvRows.join("\n"));
    const link = document.createElement("a");
    link.setAttribute("href", csvContent);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSingleProjectReport = (project) => {
    try {
      const headers = [
        "Client", "Work", "Project Code", "Location", "Assigned Team", "KM", "Office Lead", 
        "Site Lead", "Field Activity Start", "Field Activity End", "KML", "Report", "Submission", "Bill", "Status"
      ];

      const compiledUsers = project.project_assignments && project.project_assignments.length > 0
        ? project.project_assignments.map((pa) => pa.users?.name || pa.users?.email).filter(Boolean).join("; ")
        : "Unassigned";

      const rowData = [
        `"${(project.project_meta?.client_name || "").replace(/"/g, '""')}"`,
        `"${(project.project_name || "").replace(/"/g, '""')}"`,
        `"${(project.project_code || "").replace(/"/g, '""')}"`,
        `"${(project.location || "").replace(/"/g, '""')}"`,
        `"${compiledUsers.replace(/"/g, '""')}"`,
        `"${(project.km || "").replace(/"/g, '""')}"`,
        `"${(project.office_lead || "").replace(/"/g, '""')}"`,
        `"${(project.site_lead || "").replace(/"/g, '""')}"`,
        `"${(project.field_activity || "").replace(/"/g, '""')}"`,
        `"${(project.field_activity_end || "").replace(/"/g, '""')}"`,
        `"${(project.kml || "").replace(/"/g, '""')}"`,
        `"${(project.report || "").replace(/"/g, '""')}"`,
        `"${(project.submission || "").replace(/"/g, '""')}"`,
        `"${(project.bill || "").replace(/"/g, '""')}"`,
        `"${(project.status || "Backlog")}"`
      ];

      const safeProjectName = (project.project_name || "Project").replace(/[^a-z0-9]/gi, '_').toLowerCase();
      generateCSVBlob(headers, [rowData], `${safeProjectName}_report.csv`);
    } catch (err) {
      alert("Export failed: " + err.message);
    }
  };

  const downloadAllProjectsReport = async () => {
    try {
      const { data: masterList, error } = await supabase
        .from("projects")
        .select(`
          *,
          project_meta ( client_name ),
          project_assignments ( users ( name ) )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!masterList || masterList.length === 0) return alert("No project records found to download.");

      const headers = [
        "S.No", "Client", "Work", "Assigned Users", "KM", "Office Lead", 
        "Site Lead", "Field Activity Start", "Field Activity End", "KML", "Report", "Submission", "Bill", "Status"
      ];

      const rowsData = masterList.map((p, idx) => {
        const compiledUsers = p.project_assignments
          ? p.project_assignments.map((pa) => pa.users?.name).filter(Boolean).join("; ")
          : "Unassigned";

        return [
          idx + 1,
          `"${(p.project_meta?.client_name || "").replace(/"/g, '""')}"`,
          `"${(p.project_name || "").replace(/"/g, '""')}"`,
          `"${compiledUsers.replace(/"/g, '""')}"`,
          `"${(p.km || "").replace(/"/g, '""')}"`,
          `"${(p.office_lead || "").replace(/"/g, '""')}"`,
          `"${(p.site_lead || "").replace(/"/g, '""')}"`,
          `"${(p.field_activity || "").replace(/"/g, '""')}"`,
          `"${(p.field_activity_end || "").replace(/"/g, '""')}"`,
          `"${(p.kml || "").replace(/"/g, '""')}"`,
          `"${(p.report || "").replace(/"/g, '""')}"`,
          `"${(p.submission || "").replace(/"/g, '""')}"`,
          `"${(p.bill || "").replace(/"/g, '""')}"`,
          `"${(p.status || "Backlog")}"`
        ];
      });

      generateCSVBlob(headers, rowsData, `Master_Projects_Report_${new Date().toISOString().slice(0,10)}.csv`);
    } catch (err) {
      alert("Export failed: " + err.message);
    }
  };

  // ---------------- SORT, FILTER + GROUP ----------------
  const projectsByColumn = useMemo(() => {
    const map = { backlog: [], todo: [], inprogress: [], review: [], done: [] };

    // 1. Filtered data based on search bar rules
    const filtered = projectsList.filter((p) => {
      const assignedNamesString = p.project_assignments
        ? p.project_assignments.map(pa => pa.users?.name).join(" ")
        : "";

      return [
        p.project_name,
        p.project_code,
        p.location,
        p.project_meta?.client_name,
        p.office_lead,      
        p.site_lead,
        assignedNamesString 
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });

    // 2. Custom Sorting Logic: Prioritize active statuses and recent start dates
    const sorted = [...filtered].sort((a, b) => {
      const activeStatuses = ["In Progress", "Review"];
      const aIsActive = activeStatuses.includes(a.status);
      const bIsActive = activeStatuses.includes(b.status);

      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      // Secondary sorting priority: Sort by field_activity (Project Start Date) descending
      const dateA = a.field_activity ? new Date(a.field_activity) : new Date(0);
      const dateB = b.field_activity ? new Date(b.field_activity) : new Date(0);
      return dateB - dateA; 
    });

    // 3. Map items into columns
    sorted.forEach((p) => {
      const key = (p.status || "Backlog").toLowerCase().replace(/\s/g, "");
      if (map[key]) map[key].push(p);
      else map.backlog.push(p);
    });

    return map;
  }, [projectsList, searchTerm]);

  // Helper handling page jumps per individual layout row
  const handlePageChange = (columnId, direction) => {
    setColumnPages(prev => {
      const currentPage = prev[columnId] || 1;
      const nextPage = direction === "next" ? currentPage + 1 : currentPage - 1;
      return { ...prev, [columnId]: Math.max(1, nextPage) };
    });
  };

  if (authLoading) return <div>Loading auth...</div>;
  if (loading) return <div>Loading projects...</div>;

  return (
    <div className="jira-container">
      <div className="header">
        <h2>Project Board</h2>
        <div className="action-buttons" style={{ display: 'flex', gap: '10px' }}>
          <button className="download-btn" onClick={downloadAllProjectsReport} style={{ backgroundColor: "#28a745", color: "white" }}>
            📥 Download All Projects
          </button>
          {isSuperAdmin && (
            <button onClick={() => setShowForm(true)}>+ New Project</button>
          )}
        </div>
      </div>

      <FilterBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      <div className="board">
        {COLUMNS.map((col) => {
          const allColumnProjects = projectsByColumn[col.id] || [];
          const totalItems = allColumnProjects.length;
          const currentPage = columnPages[col.id] || 1;
          const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;

          // Slice data arrays cleanly to execute visual pagination constraints
          const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
          const paginatedProjects = allColumnProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);

          return (
            <div key={col.id} className="column">
              <h3>{col.title} ({totalItems})</h3>

              {paginatedProjects.map((p) => {
                const renderingUserNames = p.project_assignments?.length > 0
                  ? p.project_assignments.map((pa) => pa.users?.name).join(", ")
                  : "None Assigned";

                return (
                  <div key={p.project_id} className="card">
                    <div className="client">Client: {p.project_meta?.client_name}</div>
                    <div className="title">{p.project_name}</div>
                    <div className="code">{p.project_code}</div>
                    
                    <div className="metrics-summary" style={{ fontSize: "11px", margin: "5px 0", color: "#666" }}>
                      <div>📍 KM: {p.km || "N/A"}</div>
                      <div>👤 Lead: {p.office_lead || "N/A"}</div>
                    </div>

                    {/* Integrated System Dates UI Segment */}
                    <div className="project-dates" style={{ fontSize: "11px", margin: "5px 0", color: "#555", background: "#f1f3f5", padding: "4px", borderRadius: "4px" }}>
                      <div>📅 Started: {p.field_activity ? new Date(p.field_activity).toLocaleDateString() : "Pending"}</div>
                      <div>🏁 Closes: {p.field_activity_end ? new Date(p.field_activity_end).toLocaleDateString() : "Open Ended"}</div>
                    </div>

                    <div className="assigned" style={{ fontWeight: "500", color: "#333", marginBottom: "8px" }}>
                      👥 Team: {renderingUserNames}
                    </div>

                    <div className="card-actions-wrapper" style={{ marginTop: "8px", borderTop: "1px solid #eee", paddingTop: "8px" }}>
                      <button 
                        className="download-single-btn" 
                        onClick={() => downloadSingleProjectReport(p)} 
                        style={{ 
                          backgroundColor: "#f8f9fa", 
                          color: "#333", 
                          border: "1px solid #ced4da", 
                          padding: "3px 8px", 
                          fontSize: "11px", 
                          borderRadius: "4px",
                          cursor: "pointer",
                          width: "100%",
                          marginBottom: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px"
                        }}
                      >
                        📥 Download Project Report
                      </button>
                    </div>

                    {isSuperAdmin ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <select
                          value={p.status}
                          onChange={(e) => handleStatusChange(p.project_id, e.target.value)}
                          style={{ width: "100%" }}
                        >
                          {COLUMNS.map((c) => (
                            <option key={c.id} value={c.dbValue}>{c.title}</option>
                          ))}
                        </select>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <button onClick={() => handleEditProject(p)} style={{ flex: 1 }}>Edit</button>
                          <button onClick={() => handleDeleteProject(p.project_id)} style={{ flex: 1, backgroundColor: "#dc3545", color: "white" }}>Delete</button>
                        </div>
                      </div>
                    ) : (
                      <div className="view-only-status">
                        <span>Status: <strong>{p.status}</strong></span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Dynamic Column-Level Pagination Navigation System */}
              {totalItems > ITEMS_PER_PAGE && (
                <div className="column-pagination" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", padding: "4px" }}>
                  <button 
                    disabled={currentPage === 1} 
                    onClick={() => handlePageChange(col.id, "prev")}
                    style={{ padding: "2px 8px", fontSize: "12px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                  >
                    ◀ Prev
                  </button>
                  <span style={{ fontSize: "11px", color: "#666" }}>{currentPage} / {totalPages}</span>
                  <button 
                    disabled={currentPage === totalPages} 
                    onClick={() => handlePageChange(col.id, "next")}
                    style={{ padding: "2px 8px", fontSize: "12px", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                  >
                    Next ▶
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ProjectModal
        isOpen={showForm}
        editingProject={editingProject}
        formData={formData}
        setFormData={setFormData}
        clients={clients} 
        systemUsers={systemUsers}     
        isSuperAdmin={isSuperAdmin}   
        onSave={handleSaveProject}
        onClose={resetForm}
      />
    </div>
  );
}

export default ProjectPage;